/**
 * "Put a QR sticker on a machine" — printing the label, and what the crew does with it.
 *
 * THE TWO PEOPLE IN THIS STORY, AND ONLY ONE OF THEM TAKES THE TOUR.
 *
 * A sticker is a QR label glued to an excavator. The person who takes this tour is the one who
 * decides which machines carry an inspection sheet, prints the label and sticks it on — an equipment
 * manager, standing in the yard, logged in. The person who SCANS it is a field hand who has no login
 * at all: they point a phone camera at the sticker and type a badge number and a PIN. A click-through
 * tour can never reach that second person, which is exactly why steps 2 and 9 exist — the equipment
 * manager has to understand a screen they will never see, because what they set here is what the
 * crew is handed in a car park.
 *
 * SCOPE — WHO THIS IS OFFERED TO.
 *
 * Every step is gated on `fleet:manage` ("Manage Vehicles & Rentals"), which is what the screen's own
 * header recommends and what the seeded `manager` and `superintendent` roles hold (identity backend
 * database/session.py:283, :307). A `foreman` holds fleet:view but NOT fleet:manage, so they get the
 * screen's refusal card rather than the screen — they are correctly never offered this tour.
 *
 * The server's real rule is a UNION, not this single grant: `require_any_permission(FleetPermission
 * .MANAGE, FormsPermission.ASSIGN)` (forms/backend/app/api/routes/forms_settings.py:74), and the
 * screen mirrors it (`canByFleet || canByForms`). The tour deliberately does not, and under the
 * seeded roles that costs nothing: the only role holding `forms:assign` WITHOUT `fleet:manage` is
 * `office_staff`, whose module list has no `fleet` in it (session.py:387), so `module: 'fleet'`
 * removes them a line earlier anyway — they have no Fleet tab to start from. If somebody is ever
 * given Fleet nav on a forms-only grant, they will simply not be offered this tour; they will not be
 * offered a broken one. That is the safe direction to be wrong in, and it is the reason a step
 * carries one permission rather than the engine growing an any-of gate for a single caller.
 *
 * WHERE IT STOPS. At the row actions, having printed something real. Revoking a sticker outright, and
 * reading the inspections that come back through it, are different jobs on different screens and
 * belong in their own short tours.
 *
 * ⚠ THE ONE STEP THAT DOES NOT FIRE THE APP'S HANDLER is `when-one-goes-bad` (advanceOn NEXT).
 * Everything else here waits on the reader actually doing the thing, because the premise of the
 * feature is that the new hire ends up with a sticker they actually printed. Replace is the
 * exception: it is a step ABOUT a button rather than a step that presses one. Explaining what it is
 * for is the whole content of it, so there is nothing to wait for and Next is honest. (The button
 * stays clickable through the spotlight — it always does — and the screen's own window.confirm says
 * what will happen if they do press it.)
 *
 * ══ WHY EVERY OTHER STEP WAS SPAMMABLE, AND WHAT REPLACED IT ════════════════════════════════════
 *
 * Six of the nine steps were `advanceOn: CLICK`, which in the old engine only ADDED a click listener
 * — Next stayed live beside it as "an escape hatch". Nine presses of Next therefore ran this tour to
 * "Done" and the launcher then reported "Put a QR sticker on a machine — Finished" with a green tick,
 * having created nothing. That is the owner's "something a user can spam next on to get through",
 * and on this tour it ends with somebody believing a machine is labelled when it is not.
 *
 * The three steps that matter now wait for evidence rather than for a click:
 *
 *   start-a-sticker   waits for the DIALOG. A click that missed used to advance the tour onto steps
 *                     that live inside a dialog which never opened.
 *   issue-it          waits for the REQUEST. `submitNew` refuses locally and issues nothing when a
 *                     field is unanswered (EquipmentStickers.jsx:185-199), so a click gate walked the
 *                     reader on to "Print it" from a create that never happened — and the print sheet
 *                     the next step points at was not there either.
 *   back-to-the-list  waits for the print sheet to be GONE, so "close it" means it closed.
 *
 * `print-it` stays a click, and deliberately: it calls window.print(), which hands over to the
 * browser's own print dialog and leaves nothing behind in the page for a gate to observe. The click
 * IS the whole of that step.
 *
 * `issue-it` is also the step that carries the tour's one unavoidable dependency: everything after it
 * exists only because a sticker was created. Waiting on the request rather than the click is what
 * makes the rest of the tour true.
 */
import { defineTour, AUDIENCE, ADVANCE_ON } from '../tourSchema'

export default defineTour({
  id: 'equipment-sticker',
  title: 'Put a QR sticker on a machine',
  summary: 'Print the label a crew scans, and see what they get when they do.',
  audience: AUDIENCE.ONBOARDING,
  module: 'fleet',

  steps: [
    {
      id: 'open-stickers',
      anchor: 'fleet.nav.stickers',
      route: '/fleet',
      module: 'fleet',
      permission: 'fleet:manage',
      title: 'Start here',
      body: 'A sticker is a QR label you print and glue to a machine. Anyone can scan it with a phone camera — no app, no login — and that machine’s safety sheet opens. Open Stickers to make one.',
      // Waits for the sticker screen itself. A nav click that did not land used to carry the tour to
      // a step whose button is on a page the reader never reached.
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'equipment-sticker.new' },
    },
    {
      id: 'who-can-sign',
      anchor: 'equipment-sticker.signing-rule',
      route: '/fleet/stickers',
      module: 'fleet',
      permission: 'fleet:manage',
      title: 'Check this before you print anything',
      body: 'Nobody scanning a sticker is logged in, so this decides what the phone asks them first. On badge number and PIN, every hand needs a PIN issued to them before they can sign anything — worth knowing now rather than after the labels are on the machines.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'start-a-sticker',
      anchor: 'equipment-sticker.new',
      route: '/fleet/stickers',
      module: 'fleet',
      permission: 'fleet:manage',
      title: 'Make one',
      body: 'Nothing is created yet — this just opens the questions it needs answered.',
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'equipment-sticker.new-dialog' },
    },
    {
      id: 'fill-it-in',
      // No `route`: this step lives inside a dialog on the screen you are already on. Giving it a
      // route would let the engine "helpfully" navigate to /fleet/stickers, remounting the page and
      // closing the very dialog the step is pointing into.
      screen: '/fleet/stickers#new-dialog',
      // ONE step for BOTH fields, and it is not a copy decision — see the comment on the <form> in
      // EquipmentStickers.jsx. The overlay dims by drawing panels around the anchored element's box,
      // and a SearchableSelect's option list is absolutely positioned, so it hangs outside the box of
      // the field that owns it. Anchor one field and that field's own dropdown opens underneath the
      // dim, where it cannot be clicked. Anchoring the dialog is what keeps both pickers usable.
      // Splitting this back into "which machine" and "which sheet" would read better and would not
      // work.
      anchor: 'equipment-sticker.new-dialog',
      module: 'fleet',
      permission: 'fleet:manage',
      title: 'Answer the questions',
      body: 'First, what the sticker is for. Nearly always it is a machine in your fleet — pick it, and the sheet opens already knowing which machine it is, and the hand cannot change that. The other option is for rentals that are never in your list: one blank sticker for the job box, where the hand types the machine in himself. Then choose which sheet it opens. Only published forms are offered, and only ones somebody with no login could actually finish — that is usually why a form you were expecting is not there.',
      // STILL A NEXT, and that is not a leftover. This step has nothing to press: it explains what
      // the boxes mean. The step below is what makes the reader fill them in — it waits for the
      // create request, and `submitNew` issues nothing while a field is unanswered, so an unfilled
      // dialog holds the tour there with the screen's own red reason on show. Gating THIS step on
      // "the fields are full" would need a `filled` on the dialog anchor, and the first input in that
      // dialog is now the "for a machine" radio, which is checked by default — the gate would satisfy
      // itself on arrival and teach nothing.
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'issue-it',
      screen: '/fleet/stickers#new-dialog',
      anchor: 'equipment-sticker.submit',
      module: 'fleet',
      permission: 'fleet:manage',
      title: 'Create it',
      body: 'This makes the sticker’s code and opens the print sheet straight away. The code is random rather than the machine’s number, which is what makes a damaged or peeled sticker cheap to replace later.',
      // The request, not the click — see the header. This is also the step that enforces the one
      // above: press it with a box unanswered and nothing is issued, so the tour waits here.
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'POST /forms-settings/stickers' },
      stuckHint: 'Still here? The dialog is showing what is missing in red — usually the form it opens, or the machine.',
    },
    {
      id: 'print-it',
      screen: '/fleet/stickers#print-sheet',
      anchor: 'equipment-sticker.print-sheet.print',
      module: 'fleet',
      permission: 'fleet:manage',
      title: 'Print it',
      body: 'Set the printer to 100% or Actual size — “fit to page” shrinks the code, and a smaller code has to be scanned from closer. The machine’s number is printed large next to it, so anyone walking past can see whether a label ended up on the wrong machine. Use weatherproof stock, or print on paper and laminate it.',
      advanceOn: ADVANCE_ON.CLICK,
    },
    {
      id: 'back-to-the-list',
      screen: '/fleet/stickers#print-sheet',
      anchor: 'equipment-sticker.print-sheet.close',
      module: 'fleet',
      permission: 'fleet:manage',
      title: 'Close the sheet',
      body: 'That was the print preview, not a page you have to keep. Closing it puts you back on the list, with the sticker you just made on it.',
      // Waits for the sheet to have actually gone, because the next step points at a row on the list
      // underneath it. Advancing on the click alone would spotlight a row behind a print sheet that
      // was still up.
      advanceOn: ADVANCE_ON.DO,
      until: { gone: 'equipment-sticker.print-sheet.print' },
    },
    {
      id: 'what-a-hand-sees',
      anchor: 'equipment-sticker.row.preview',
      route: '/fleet/stickers',
      module: 'fleet',
      permission: 'fleet:manage',
      title: 'See what the crew sees',
      body: 'Preview opens the exact page a phone gets when it scans that machine’s sticker. Do it once — it is the only way to know what you are asking people to fill in out in the yard. It opens in a new tab; come back to this one to finish.',
      advanceOn: ADVANCE_ON.CLICK,
    },
    {
      id: 'when-one-goes-bad',
      anchor: 'equipment-sticker.row.replace',
      route: '/fleet/stickers',
      module: 'fleet',
      permission: 'fleet:manage',
      title: 'When a sticker goes bad',
      body: 'Peeled, painted over, unreadable, stuck on the wrong machine, or photographed by somebody who should not have it — Replace prints a new code and kills the old one in one go. There is no way to edit a sticker on purpose: the old code stops working for good instead of being handed to another machine. The machine and every inspection already filed for it are untouched.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    /**
     * A CLOSING STEP, AND IT IS NOT DECORATION.
     *
     * `when-one-goes-bad` above spotlights Replace on a REAL sticker on a REAL machine, and it
     * advances on NEXT — so if it were the last step, pressing Next would end the tour, which
     * switches practice mode off, leaving that button live under the light the reader has spent nine
     * steps learning to trust. Replace mints a new code AND revokes the one currently on the machine,
     * so a scan of the label already glued to it leads nowhere. window.confirm gates it, which is why
     * this is the smaller of the two closing steps and not an emergency — but a tour should not end
     * with its spotlight on a destructive control that goes live at that instant.
     */
    {
      id: 'thats-the-loop',
      anchor: 'equipment-sticker.list',
      route: '/fleet/stickers',
      module: 'fleet',
      permission: 'fleet:manage',
      title: 'That is the loop',
      body: 'Issue the sticker, print it, put it on the machine — and from then on the crew fills the sheet in from their phone without a login. The one thing worth checking before you print a box of them is this panel: it decides what a hand has to type before their answers count as theirs, and every badge and PIN it asks for has to exist before the labels go out.',
      advanceOn: ADVANCE_ON.NEXT,
    },
  ],
})
